/**
 * ProtocolloInput — F-008 Phase 6 (IMPL-P6-004).
 *
 * Input controllato react-hook-form per il numero di protocollo Agenzia
 * delle Entrate. Formato atteso: `RLI/MM/YYYY/NNNNNN`.
 *
 * Pattern di riferimento:
 *  - `apps/web/src/features/profilo/components/CFInput.tsx` (uso di
 *    `useFormContext` + `Controller` + `aria-describedby` linking)
 *  - PHASE_6_PLAN.md IMPL-P6-004 (snippet di riferimento)
 *
 * Accessibilità (WCAG 2.1 AA):
 *  - `<Label>` collegata via `htmlFor` (criterion 1.3.1, 3.3.2)
 *  - `aria-describedby` punta sempre all'hint id; quando l'errore è presente,
 *    aggiunge anche l'error id (criterion 3.3.1, 3.3.3)
 *  - `aria-invalid` true quando errore (criterion 4.1.2)
 *  - errore renderizzato con `role="alert"` per annuncio screen reader
 */
import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const HINT_ID = 'protocollo-ade-hint';
const ERROR_ID = 'protocollo-ade-error';

export interface ProtocolloInputProps {
  disabled?: boolean;
  /**
   * Override id per supportare istanze multiple del componente nello stesso
   * documento (raro, ma sicuro per i test). Default: `protocolloAde`.
   */
  inputId?: string;
}

export function ProtocolloInput({
  disabled,
  inputId = 'protocolloAde',
}: ProtocolloInputProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const error = errors.protocolloAde;
  const hasError = Boolean(error);
  const errorMessage = error && 'message' in error ? String(error.message) : '';

  const describedBy = hasError ? `${HINT_ID} ${ERROR_ID}` : HINT_ID;

  return (
    <div className="space-y-1">
      <Label htmlFor={inputId}>Numero di protocollo AdE</Label>
      <Input
        id={inputId}
        type="text"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        placeholder="RLI/05/2026/000001"
        aria-describedby={describedBy}
        aria-invalid={hasError}
        aria-required="true"
        className={cn('font-mono', hasError && 'border-destructive')}
        {...register('protocolloAde')}
      />
      <p id={HINT_ID} className="text-xs text-muted-foreground">
        Formato: RLI/MM/YYYY/NNNNNN (es. RLI/05/2026/000001)
      </p>
      {hasError && (
        <p
          id={ERROR_ID}
          role="alert"
          className="text-xs text-destructive"
        >
          {errorMessage || 'Numero di protocollo non valido'}
        </p>
      )}
    </div>
  );
}
